import { Injectable, inject } from '@angular/core';
import { LocationNextApi } from '@fmnts/api/next';
import { createHttpRequestEffectHandler } from '@fmnts/common/store';
import { hasProperty, isString, matchOption } from '@fmnts/core';
import { fromHttpOrGeneralError } from '@formunauts/shared/errors/domain';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import moment from 'moment';
import { EMPTY, concatMap, delay, filter, map, of, switchMap } from 'rxjs';
import {
  NextLocationApiActions,
  NextLocationListPageActions,
} from './next-locations.actions';
import { selectRefresh } from './next-locations.selectors';

const hasNextPage = hasProperty('next', isString);

@Injectable()
export class NextLocationsApiEffects {
  private readonly actions$ = inject(Actions);
  private readonly locationApi = inject(LocationNextApi);
  private readonly store = inject(Store);

  /**
   * Fetches location details.
   */
  getLocation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(NextLocationApiActions.getLocation.request),
      // TODO: rethink which mapping operator to use
      concatMap(
        createHttpRequestEffectHandler(NextLocationApiActions.getLocation, {
          run: ({ locationId }) => this.locationApi.get(locationId),
          onError: fromHttpOrGeneralError('fmnts'),
        }),
      ),
    );
  });

  /**
   * Fetches list of locations.
   */
  getLocationList$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(NextLocationApiActions.getLocationList.request),
      switchMap(
        createHttpRequestEffectHandler(NextLocationApiActions.getLocationList, {
          run: ({ query }) => this.locationApi.list(query),
          onError: fromHttpOrGeneralError('fmnts'),
        }),
      ),
    );
  });

  fetchAllPagesofLocationList$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(NextLocationApiActions.getLocationList.success),
      filter((response) => hasNextPage(response.payload)),
      map((response) => {
        const { pageSize, currentPage } = response.payload;
        return NextLocationApiActions.getLocationList.request({
          query: { page: currentPage + 1, page_size: pageSize },
        });
      }),
    );
  });

  /**
   * Fetches list of locations.
   */
  getLocationListAll$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(NextLocationApiActions.getLocationListAll.request),
      switchMap(
        createHttpRequestEffectHandler(
          NextLocationApiActions.getLocationListAll,
          {
            run: ({ query }) => this.locationApi.listAll(query),
            onError: fromHttpOrGeneralError('fmnts'),
          },
        ),
      ),
    );
  });

  /**
   * Persist location check-in on server.
   */
  checkInToLocation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(NextLocationApiActions.checkIn.request),
      switchMap(
        createHttpRequestEffectHandler(NextLocationApiActions.checkIn, {
          run: ({ checkIn }) => this.locationApi.checkIn(checkIn),
          onError: (error, { checkIn }) =>
            fromHttpOrGeneralError('fmnts')(error).pipe(
              map((value) => ({ ...value, checkIn })),
            ),
        }),
      ),
    );
  });

  /**
   * Reset the list of locations at the end of day
   */
  resetLocationsOnDateChanged$ = createEffect(() => {
    return this.store.select(selectRefresh).pipe(
      switchMap(
        matchOption({
          onSome: (value: string) =>
            of(NextLocationListPageActions.reset()).pipe(
              delay(moment(value).endOf('day').toDate()),
            ),
          onNone: () => EMPTY,
        }),
      ),
    );
  });

  fetchAfterReset$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(NextLocationListPageActions.reset),
      map(() =>
        NextLocationApiActions.getLocationList.request({
          query: { page_size: 1000 },
        }),
      ),
    );
  });
}
